import { useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Text,
  VStack,
  useBreakpointValue
} from '@chakra-ui/react'
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons'
import Errors from 'configs/errors'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import AuthLayout from 'components/Layouts/AuthLayout'
import AuthBackButton from 'components/Layouts/AuthLayout/AuthBackButton'
import AuthLogo from 'components/Layouts/AuthLayout/AuthLogo'
import {
  authButtonProps,
  authInputProps,
  authLabelColor,
  authLinkColor,
  authMutedColor,
  authTitleColor
} from 'components/Layouts/AuthLayout/constants'
import AuthOrDivider from 'components/Layouts/AuthLayout/AuthOrDivider'
import GoogleSignInButton from 'components/GoogleSignInButton'
import AuthGoogleRequiredModal from 'components/AuthGoogleRequiredModal'
import { checkEmail } from 'API/auth'
import { AUTH_ERROR_CODES } from 'constants/auth-errors'
import { EMAIL_VERIFY_BYPASS_SESSION_KEY } from 'constants/auth-session'
import { validateInvitation } from 'API/invitation'
import { AuthenticateParams } from 'configs/constants/enums'
import routes from 'routes'

// 'email' → enter email, check if exists
// 'login' → existing user: enter password
// 'register' → new user: go to CreateAccountPage with email prefilled
type Step = 'email' | 'login'

/** Minimal shape of an axios-like error for safe narrowing inside catch blocks. */
type ApiError = {
  response?: { status?: number; data?: { error?: { message?: string } } }
  message?: string
}

interface ILoginForm {
  email: string
  password: string
}

const LoginPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [step, setStep] = useState<Step>('email')
  const [submitting, setSubmitting] = useState(false)
  const [checking, setChecking] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showGoogleRequiredModal, setShowGoogleRequiredModal] = useState(false)
  const { authStore } = useStores()
  const isMobileAuthChrome = useBreakpointValue({ base: true, lg: false }, { fallback: 'lg', ssr: false })
  const redirectPath = searchParams.get('redirect')
  const prefillEmail = searchParams.get('email') ?? ''

  const { register, handleSubmit, getValues, setValue, formState: { errors } } = useForm<ILoginForm>({
    mode: 'onChange',
    defaultValues: { email: prefillEmail }
  })

  useEffect(() => {
    // If the user lands here while still logged in (e.g. clicked an invitation
    // email link), do NOT wipe the session — redirect them straight to
    // /select-profile, preserving any query string (invitation token, prefill
    // email, etc.) so the downstream flow can pick it up.
    // ?logout=1 is handled separately by index.tsx; by the time LoginPage
    // mounts on a logout flow, the token is already gone so this branch
    // naturally falls through.
    const existingToken = localStorage.getItem(AuthenticateParams.ACCESS_TOKEN)
    if (existingToken) {
      // Arriving via an invitation email link (?email=) while already logged in
      // IS proof of email ownership — set the verify-email bypass so the
      // EmailVerifyGuard on /select-profile lets an unverified account through to
      // the dashboard instead of detouring to /verify-email (the reported bug).
      if (prefillEmail) {
        sessionStorage.setItem(EMAIL_VERIFY_BYPASS_SESSION_KEY, '1')
      }
      const qs = searchParams.toString()
      navigate(`${routes.selectProfile.value}${qs ? `?${qs}` : ''}`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const handleContinue = handleSubmit(async ({ email }) => {
    // Re-entry guard: button is disabled via isLoading, but Enter-key also
    // calls this — multiple rapid Enters could fire before setChecking(true)
    // commits, producing duplicate /auth/check-email requests.
    if (checking) return
    setChecking(true)
    try {
      const { exists } = await checkEmail(email)
      if (exists) {
        setStep('login')
      } else {
        // New user — go to register page with email prefilled. The invitation token
        // rides along so registering auto-joins the org it was issued for; dropping it
        // here is what used to force a second accept from the notification bell.
        const invitationToken = searchParams.get('invitation')
        navigate(
          `${routes.createAccount.value}?email=${encodeURIComponent(email)}` +
            `${redirectPath ? `&redirect=${encodeURIComponent(redirectPath)}` : ''}` +
            `${invitationToken ? `&invitation=${encodeURIComponent(invitationToken)}` : ''}`
        )
      }
    } catch {
      toast.error('Something went wrong. Please try again.')
    } finally {
      setChecking(false)
    }
  })

  // Step 2 — login with password
  const handleLogin = handleSubmit(async ({ email, password }) => {
    // Same re-entry guard as handleContinue — Enter-key bypasses isLoading.
    if (submitting) return
    setSubmitting(true)
    try {
      const result = await authStore.login(email, password)
      if (authStore.resetPasswordToken && !result) {
        toast.warning('Your account has been reset. Please enter a new password.')
        navigate(`${routes.resetPassword.value}?token=${authStore.resetPasswordToken}`)
        return
      }
      if (authStore.isDisabled && !result) {
        toast.error('Your account has been deactivated. Please contact the administrator.')
        return
      }
      // When user came from an invitation email link, clicking that link IS
      // proof of email ownership — skip the OTP verification step. Otherwise
      // we lose the invitation token to the verify-email detour and the
      // recipient can't accept the invite without re-pasting the link.
      const invitationToken = searchParams.get('invitation')
      const fromInvitationLink = !!prefillEmail || !!invitationToken
      if (authStore.userDetail?.isEmailVerified === false) {
        if (fromInvitationLink) {
          // Clicking the invitation email link proves email ownership — skip the
          // OTP step. Set the bypass so EmailVerifyGuard on /select-profile lets
          // the (still DB-unverified) account through instead of re-detouring to
          // /verify-email.
          sessionStorage.setItem(EMAIL_VERIFY_BYPASS_SESSION_KEY, '1')
        } else {
          navigate(
            `${routes.verifyEmail.value}?email=${encodeURIComponent(email)}&redirect=${encodeURIComponent(
              redirectPath ?? routes.dashboard.value
            )}&send=1`
          )
          return
        }
      }

      // Post-login routing: always go through /select-profile.
      // Preserve the full query string so invitation token + email prefill
      // survive — SelectProfilePage uses them for the highlighted card.
      const qs = searchParams.toString()
      navigate(`${routes.selectProfile.value}${qs ? `?${qs}` : ''}`)
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code
      const msg = err instanceof Error ? err.message : undefined
      if (code === AUTH_ERROR_CODES.LOGIN_REQUIRES_GOOGLE) {
        if (isMobileAuthChrome) {
          setShowGoogleRequiredModal(true)
          return
        }
        toast.error(msg ?? 'Please sign in with Google for this account.')
        return
      }
      toast.error(msg ?? 'Login failed. Please try again.')
    } finally {
      setSubmitting(false)
    }
  })

  const email = getValues('email')

  return (
    <AuthLayout>
      <AuthGoogleRequiredModal
        isOpen={showGoogleRequiredModal}
        onClose={() => setShowGoogleRequiredModal(false)}
        onSecondaryAction={() => setStep('email')}
        secondaryLabel="Back to email"
        redirectPath={redirectPath ?? undefined}
      />
      <Box
        w="calc(100% + 48px)"
        ml="-24px"
        mr="-24px"
        position="relative"
        display="flex"
        flexDirection="column"
        alignItems="center"
        minH={{ base: 'auto', lg: '100vh' }}
        justifyContent={{ base: 'flex-start', lg: 'center' }}
      >
        {/* Back arrow on login step */}
        {step === 'login' && <AuthBackButton onClick={() => setStep('email')} />}

        <VStack w="min(400px, calc(100vw - 48px))" spacing={0} align="stretch">
          {/* Logo + title */}
          <VStack spacing="12px" mb="32px" align="center">
            <AuthLogo />
            <Text fontSize="18px" fontWeight="600" color={authTitleColor} m={0} textAlign="center">
              {step === 'email' ? 'Login or Sign up' : `Login to RallyPD`}
            </Text>
            {step === 'login' && (
              <Text fontSize="16px" color={authMutedColor} m={0}>
                Enter password of{' '}
                <Text as="span" fontWeight="500" color={authLabelColor}>{email}</Text>
              </Text>
            )}
          </VStack>

          {step === 'email' ? (
          <VStack spacing="16px" align="stretch">
            <FormControl isInvalid={!!errors.email} display="flex" flexDirection="column" gap="8px">
              <Text fontSize="16px" fontWeight="500" color={authLabelColor} m={0}>Email</Text>
              <Input
                placeholder="Enter your email"
                type="email"
                autoFocus
                {...authInputProps}
                {...register('email', {
                  required: Errors.USERNAME_REQUIRED,
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email address' }
                })}
                onKeyDown={e => e.key === 'Enter' && handleContinue()}
                // Lock the email when it came in via `?email=` (invitation link) so the
                // user can't accidentally re-key a different address and miss the invite.
                isReadOnly={!!prefillEmail}
                cursor={prefillEmail ? 'not-allowed' : undefined}
                opacity={prefillEmail ? 0.85 : 1}
              />
              <FormErrorMessage mt="0">{errors.email?.message}</FormErrorMessage>
            </FormControl>

            <Button onClick={handleContinue} isLoading={checking} {...authButtonProps}>
              Continue
            </Button>

            <AuthOrDivider />
            <GoogleSignInButton
              redirectPath={redirectPath ?? undefined}
              expectedEmail={prefillEmail || undefined}
              invitationToken={searchParams.get('invitation') || undefined}
            />
          </VStack>
          ) : (
          <VStack spacing="16px" align="stretch">
            <FormControl isInvalid={!!errors.password} display="flex" flexDirection="column" gap="8px">
              <Text fontSize="16px" fontWeight="500" color={authLabelColor} m={0}>Password</Text>
              <InputGroup>
                <Input
                  placeholder="••••••••"
                  type={showPassword ? 'text' : 'password'}
                  autoFocus
                  autoComplete="current-password"
                  {...authInputProps}
                  {...register('password', { required: Errors.PASSWORD_REQUIRED })}
                  onKeyDown={e => e.key === 'Enter' && handleLogin()}
                />
                <InputRightElement h="48px">
                  <IconButton
                    aria-label="Toggle password visibility"
                    icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPassword(v => !v)}
                    color="whiteAlpha.700"
                    bg="transparent"
                    border="none"
                    _hover={{ bg: 'transparent', color: 'white' }}
                  />
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage mt="0">{errors.password?.message}</FormErrorMessage>
            </FormControl>

            <Flex justify="flex-start">
              <Link to={routes.forgotPassword.value}>
                <Text fontSize="14px" color={authLinkColor} fontWeight="600" m={0}>
                  Forgot your password?
                </Text>
              </Link>
            </Flex>

            <Button onClick={handleLogin} isLoading={submitting} {...authButtonProps}>
              Log in
            </Button>
          </VStack>
          )}
        </VStack>
      </Box>
    </AuthLayout>
  )
}

export default observer(LoginPage)
